import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import localize from "../../../common/localize";
import { getInputLabelProps } from "../../../common/appStyles";
import { EMPTY_SETTLEMENT_FILTERS } from "./settlementFilterUtils";

export default function SettlementFilters({ options, filters, setFilters }) {
  const update = (key, values) =>
    setFilters((prev) => ({ ...prev, [key]: values }));

  const hasOptions =
    options.cities.length > 0 ||
    options.states.length > 0 ||
    options.personals.length > 0 ||
    options.events.length > 0 ||
    options.paymentTypes.length > 0;

  if (!hasOptions) return null;

  // Keep field size fixed; truncate long selected values (chip labels) and
  // keep chips on a single row so height never grows.
  const filterSx = {
    minWidth: 220,
    flex: 1,
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
      overflowX: "hidden",
    },
    "& .MuiAutocomplete-inputRoot .MuiChip-root": { maxWidth: 150 },
  };

  const stringFilter = (label, optionList, key) => (
    <Autocomplete
      multiple
      limitTags={2}
      size="small"
      options={optionList}
      value={filters[key]}
      onChange={(e, val) => update(key, val)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputLabelProps={getInputLabelProps()}
        />
      )}
      sx={filterSx}
    />
  );

  return (
    <Box className="flex flex-col gap-2" sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#374151" }}>
        {localize.settlement_filters_title}
      </Typography>
      <Box className="flex flex-row flex-wrap gap-2 items-center">
        {options.cities.length > 0 &&
          stringFilter(localize.city, options.cities, "cities")}
        {options.states.length > 0 &&
          stringFilter(localize.department, options.states, "states")}
        {options.personals.length > 0 && (
          <Autocomplete
            multiple
            limitTags={2}
            size="small"
            options={options.personals}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={options.personals.filter((p) =>
              filters.personalIds.includes(p.id),
            )}
            onChange={(e, val) =>
              update(
                "personalIds",
                val.map((v) => v.id),
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={localize.personal}
                InputLabelProps={getInputLabelProps()}
              />
            )}
            sx={filterSx}
          />
        )}
        {options.events.length > 0 && (
          <Autocomplete
            multiple
            limitTags={2}
            size="small"
            options={options.events}
            getOptionLabel={(o) => o.code}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={options.events.filter((ev) =>
              filters.eventIds.includes(ev.id),
            )}
            onChange={(e, val) =>
              update(
                "eventIds",
                val.map((v) => v.id),
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={localize.service_order}
                InputLabelProps={getInputLabelProps()}
              />
            )}
            sx={filterSx}
          />
        )}
        {options.paymentTypes.length > 0 &&
          stringFilter(localize.payment_type, options.paymentTypes, "paymentTypes")}
        <Button
          size="small"
          variant="text"
          sx={{ color: "#10366E" }}
          onClick={() => setFilters(EMPTY_SETTLEMENT_FILTERS)}
        >
          {localize.settlement_clear_filters}
        </Button>
      </Box>
    </Box>
  );
}
